import { useWatch } from "react-hook-form";

import { Box } from "ui/box";
import { Typography } from "ui/typography";
import { FieldSkeleton } from "containers/admin/clients/client/theme-configuration-page/components/field-skeleton/field-skeleton";
import { AdobeFontsField } from "containers/admin/clients/client/theme-configuration-page/brand-identity/fonts-tab/adobe-fonts-field";
import { FontFamilyPreview } from "containers/admin/clients/client/theme-configuration-page/brand-identity/fonts-tab/font-family-preview";
import { GoogleFontsFields } from "containers/admin/clients/client/theme-configuration-page/brand-identity/fonts-tab/google-fonts-fields";

import { useCurrentClient } from "hooks/api/use-clients";
import { useGoogleFontsQuery, useTypekitKitQuery } from "hooks/api/use-theme";

import {
  getAdobeFontPreviews,
  getGoogleFontPreviews,
} from "containers/admin/clients/client/theme-configuration-page/brand-identity/fonts-tab/fonts-tab.helpers";

import type { BrandIdentityFormValues } from "containers/admin/clients/client/theme-configuration-page/brand-identity/brand-identity.types";

import {
  BODY_SX,
  FIELD_LIST_SX,
  FIELDS_SX,
  PREVIEW_SX,
  TAB_SX,
} from "containers/admin/clients/client/theme-configuration-page/brand-identity/fonts-tab/fonts-tab.styles";

type FontsTabProps = {
  isEditing: boolean;
  isLoading: boolean;
};

export const FontsTab = ({ isEditing, isLoading }: FontsTabProps) => {
  const client = useCurrentClient();
  const { adobeFontsProjectId, googleFonts: selectedGoogleFonts } = useWatch<
    BrandIdentityFormValues,
    "fonts"
  >({ name: "fonts" });
  const { data: googleFonts = [] } = useGoogleFontsQuery();
  const { data: typekitKit } = useTypekitKitQuery(client.id, adobeFontsProjectId);

  // How many Google fonts are saved is not known yet, so one row stands in for the list.
  if (isLoading) {
    return (
      <Box aria-busy sx={TAB_SX}>
        <Typography variant="h4" component="h3">
          Font family
        </Typography>
        <Box sx={FIELDS_SX}>
          <Box sx={FIELD_LIST_SX}>
            <FieldSkeleton label="Adobe fonts Project ID" />
            <FieldSkeleton label="Google font" />
          </Box>
        </Box>
      </Box>
    );
  }

  const previews = [
    ...getGoogleFontPreviews(
      selectedGoogleFonts.map(({ family }) => family).filter(Boolean),
      googleFonts,
    ),
    ...(adobeFontsProjectId && typekitKit
      ? getAdobeFontPreviews(adobeFontsProjectId, typekitKit.kit.families)
      : []),
  ];

  const stylesheetUrls = Array.from(new Set(previews.map(({ stylesheetUrl }) => stylesheetUrl)));

  return (
    <Box sx={TAB_SX}>
      {/*
        Deliberately without `precedence`: React 19 suspends an update on a precedence stylesheet
        until it loads, which held the whole preview back behind a font CSS request.
      */}
      {stylesheetUrls.map((url) => (
        <link key={url} rel="stylesheet" href={url} />
      ))}
      <Typography variant="h4" component="h3">
        Font family
      </Typography>
      <Box sx={BODY_SX}>
        <Box sx={FIELDS_SX}>
          <Box sx={FIELD_LIST_SX}>
            <AdobeFontsField isEditing={isEditing} />
            <GoogleFontsFields
              isEditing={isEditing}
              fontFamilies={googleFonts.map(({ family }) => family)}
            />
          </Box>
        </Box>
        <Box sx={PREVIEW_SX}>
          {previews.map((font) => (
            <FontFamilyPreview key={`${font.source}-${font.name}`} font={font} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};
